import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  EnvelopeOpenIcon,
  PencilSquareIcon,
  UsersIcon,
  RectangleStackIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  KeyIcon,
  ClockIcon,
  PlusIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardIcon,
  EllipsisVerticalIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import { KeycloakContext } from '../App';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import {
  PageHeader,
  Card,
  CardContent,
  Tabs,
  Button,
  Input,
  Textarea,
  Field,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  StatusBadge,
  Badge,
  EmptyState,
  ErrorAlert,
  Skeleton,
} from '../components/ui';
import { formatDateTime } from '../lib/format';

// ─── Types ──────────────────────────────────────────────────────────────────

interface UserToken {
  id: number;
  user_id: string;
  token: string;
  credits: number;
  from_name: string;
  from_address: string;
  created_at: string;
  updated_at: string;
}

interface SentEmail {
  id: number;
  user_id: string;
  from: string;
  recipients: string[];
  subject: string;
  body_html?: string;
  body_text?: string;
  template_id?: number;
  status: 'sent' | 'failed' | 'sending';
  error?: string;
  credit_used: number;
  created_at: string;
}

interface Contact {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  attributes?: Record<string, string>;
  created_at: string;
}

interface ContactList {
  id: number;
  name: string;
  description: string;
  contact_count: number;
  created_at: string;
}

interface Template {
  id: number;
  name: string;
  subject: string;
  description: string;
  sender_name: string;
  variables: string[];
  created_at: string;
}

const CREDIT_PRICE_HT = 0.002;
const TVA = 0.20;

const formatPrice = (value: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 3 }).format(value);

type CodeLang = 'curl' | 'python' | 'javascript';
type TabKey = 'compose' | 'contacts' | 'lists' | 'templates' | 'history' | 'settings';

interface RowMenuItem {
  label: string;
  icon?: React.ReactNode;
  onSelect: () => void;
  danger?: boolean;
  separatorBefore?: boolean;
}

// RowMenu — three-dot overflow menu used across Contacts/Lists/Templates rows.
//
// Why we don't use the shared <Dropdown> here: rows live inside the <Table>
// component which wraps in `overflow-auto`, and Cards use `overflow-hidden` for
// rounded corners. A normal absolutely-positioned popover gets clipped by both.
// We render the popover into document.body via createPortal and position it
// with `position: fixed` so it can escape the table's clip region.
function RowMenu({ items }: { items: RowMenuItem[] }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const recalc = () => {
      const rect = buttonRef.current!.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    };
    recalc();

    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    // Recompute / dismiss on scroll & resize so the menu doesn't drift away.
    window.addEventListener('scroll', recalc, true);
    window.addEventListener('resize', recalc);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', recalc, true);
      window.removeEventListener('resize', recalc);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="p-1.5 rounded-md text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
        title="Actions"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <EllipsisVerticalIcon className="h-4 w-4" />
      </button>
      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 60 }}
            className="min-w-[12rem] origin-top rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-elevation-3"
            role="menu"
          >
            {items.map((item, i) => (
              <React.Fragment key={i}>
                {item.separatorBefore && <div className="my-1 h-px bg-border" />}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    item.onSelect();
                  }}
                  className={`flex w-full cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm transition-colors [&_svg]:h-4 [&_svg]:w-4 ${
                    item.danger
                      ? 'text-destructive hover:bg-destructive/10'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </button>
              </React.Fragment>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function Emailing() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const [tab, setTab] = useState<TabKey>('compose');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [userToken, setUserToken] = useState<UserToken | null>(null);
  const [emails, setEmails] = useState<SentEmail[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [lists, setLists] = useState<ContactList[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);

  const [showBuyCredits, setShowBuyCredits] = useState(false);

  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${keycloak?.token}` }),
    [keycloak?.token]
  );

  const loadAll = async () => {
    try {
      setLoading(true);
      const [tRes, eRes, cRes, lRes, tplRes] = await Promise.all([
        fetch(`${API_URL}/emailing/token`, { headers }),
        fetch(`${API_URL}/emailing/emails`, { headers }),
        fetch(`${API_URL}/emailing/contacts`, { headers }),
        fetch(`${API_URL}/emailing/lists`, { headers }),
        fetch(`${API_URL}/emailing/templates`, { headers }),
      ]);
      if (!tRes.ok) throw new Error('Failed to fetch emailing account');
      setUserToken(await tRes.json());
      setEmails(eRes.ok ? (await eRes.json()) || [] : []);
      setContacts(cRes.ok ? (await cRes.json()) || [] : []);
      setLists(lRes.ok ? (await lRes.json()) || [] : []);
      setTemplates(tplRes.ok ? (await tplRes.json()) || [] : []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader icon={<EnvelopeOpenIcon />} title="Emailing" description="Send transactional and marketing emails." />
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </CardContent>
        </Card>
      </div>
    );
  }

  const tabItems = [
    { value: 'compose', label: 'Compose', icon: <PencilSquareIcon className="h-4 w-4" /> },
    { value: 'contacts', label: 'Contacts', icon: <UsersIcon className="h-4 w-4" />, count: contacts.length },
    { value: 'lists', label: 'Lists', icon: <RectangleStackIcon className="h-4 w-4" />, count: lists.length },
    { value: 'templates', label: 'Templates', icon: <DocumentTextIcon className="h-4 w-4" />, count: templates.length },
    { value: 'history', label: 'History', icon: <ClockIcon className="h-4 w-4" />, count: emails.length },
    { value: 'settings', label: 'API & sender', icon: <KeyIcon className="h-4 w-4" /> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<EnvelopeOpenIcon />}
        title="Emailing"
        description="Send transactional emails, manage contacts, design HTML templates."
        actions={
          <div className="flex items-center gap-2">
            <div className="text-right hidden sm:block">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">Credits</div>
              <div className="text-lg font-semibold text-foreground tabular-nums">{userToken?.credits ?? 0}</div>
            </div>
            <Button onClick={() => setShowBuyCredits(true)} className="text-white">
              Order Credits
            </Button>
          </div>
        }
      />

      {error && <ErrorAlert message={error} />}

      <Tabs value={tab} onChange={(v) => setTab(v as TabKey)} items={tabItems} />

      {tab === 'compose' && (
        <ComposeTab
          headers={headers}
          token={userToken}
          templates={templates}
          contacts={contacts}
          lists={lists}
          onSent={loadAll}
          onOrderCredits={() => setShowBuyCredits(true)}
          toast={toast}
        />
      )}
      {tab === 'contacts' && (
        <ContactsTab headers={headers} contacts={contacts} onChange={loadAll} toast={toast} confirm={confirm} />
      )}
      {tab === 'lists' && (
        <ListsTab headers={headers} lists={lists} contacts={contacts} onChange={loadAll} toast={toast} confirm={confirm} />
      )}
      {tab === 'templates' && (
        <TemplatesTab headers={headers} templates={templates} onChange={loadAll} toast={toast} confirm={confirm} />
      )}
      {tab === 'history' && <HistoryTab emails={emails} />}
      {tab === 'settings' && (
        <SettingsTab headers={headers} token={userToken} onChange={loadAll} toast={toast} confirm={confirm} />
      )}

      {showBuyCredits && (
        <BuyCreditsDialog
          open={showBuyCredits}
          onClose={() => setShowBuyCredits(false)}
          headers={headers}
          toast={toast}
        />
      )}
    </div>
  );
}

// ─── Compose tab ────────────────────────────────────────────────────────────

interface ComposeProps {
  headers: Record<string, string>;
  token: UserToken | null;
  templates: Template[];
  contacts: Contact[];
  lists: ContactList[];
  onSent: () => void;
  onOrderCredits: () => void;
  toast: (msg: string, type?: any) => void;
}

function ComposeTab({ headers, token, templates, contacts, lists, onSent, onOrderCredits, toast }: ComposeProps) {
  const [recipientsInput, setRecipientsInput] = useState('');
  const [pickedListId, setPickedListId] = useState<number | ''>('');
  const [pickedListMembers, setPickedListMembers] = useState<Contact[] | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [variablesText, setVariablesText] = useState('');
  const [sending, setSending] = useState(false);

  const selectedTemplate = templates.find((t) => t.id === templateId) || null;

  // Whenever the picked list changes, fetch its members so the user sees the
  // count up front rather than discovering "list is empty" only at send time.
  useEffect(() => {
    if (pickedListId === '') {
      setPickedListMembers(null);
      return;
    }
    let cancelled = false;
    setLoadingMembers(true);
    fetch(`${API_URL}/emailing/lists/${pickedListId}/contacts`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load list contacts'))))
      .then((data: Contact[]) => {
        if (!cancelled) setPickedListMembers(data || []);
      })
      .catch(() => {
        if (!cancelled) setPickedListMembers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingMembers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pickedListId, headers]);

  const expandRecipients = (): { all: string[]; manualCount: number; listCount: number } => {
    const manual = recipientsInput
      .split(/[\s,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const fromList = (pickedListMembers || []).map((c) => c.email).filter(Boolean);
    const all = Array.from(new Set([...manual, ...fromList]));
    return { all, manualCount: manual.length, listCount: fromList.length };
  };

  const parseVariables = (): Record<string, string> | undefined => {
    const text = variablesText.trim();
    if (!text) return undefined;
    try {
      return JSON.parse(text);
    } catch {
      throw new Error('Variables must be valid JSON, e.g. {"name":"Alice"}');
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateId && !body.trim()) {
      toast('Either pick a template or write a body.', 'error');
      return;
    }
    const { all: recipients, manualCount, listCount } = expandRecipients();
    if (recipients.length === 0) {
      if (pickedListId !== '' && manualCount === 0 && listCount === 0) {
        toast('The selected list has no contacts. Add contacts to it from the Lists tab, or type recipients manually.', 'error');
      } else {
        toast('Add at least one recipient.', 'error');
      }
      return;
    }
    setSending(true);
    try {
      let variables: Record<string, string> | undefined;
      try {
        variables = parseVariables();
      } catch (err: any) {
        toast(err.message, 'error');
        return;
      }
      const payload: any = { recipients, subject, html: body };
      if (templateId !== '') payload.template_id = templateId;
      if (variables) payload.variables = variables;

      const res = await fetch(`${API_URL}/emailing/send`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.status === 402) {
        toast(data.error || 'Insufficient credits.', 'error');
        return;
      }
      if (!res.ok) {
        toast(data.error || 'Failed to send email', 'error');
        return;
      }
      toast(`Email sent to ${recipients.length} recipient(s).`, 'success');
      setRecipientsInput('');
      setPickedListId('');
      setSubject('');
      setBody('');
      setTemplateId('');
      setVariablesText('');
      onSent();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card className="lg:col-span-2">
        <CardContent className="p-6 space-y-4">
          <h3 className="text-base font-semibold">Send an email</h3>
          <p className="text-sm text-muted-foreground -mt-2">
            1 credit per email — recipients are not multiplied.
          </p>

          <form className="space-y-4" onSubmit={handleSend}>
            <Field
              label="Recipients"
              hint="Comma-, space- or newline-separated emails. Combined with the list below if you pick one (duplicates are removed)."
            >
              <Textarea
                value={recipientsInput}
                onChange={(e) => setRecipientsInput(e.target.value)}
                rows={3}
                placeholder="alice@example.com, bob@example.com"
              />
            </Field>

            <Field
              label="…or pick a list"
              hint={
                pickedListId === ''
                  ? undefined
                  : loadingMembers
                  ? 'Loading list members…'
                  : pickedListMembers && pickedListMembers.length === 0
                  ? 'This list has no contacts. Add some from the Lists tab → Manage contacts.'
                  : `${pickedListMembers?.length ?? 0} contact(s) in this list.`
              }
            >
              <select
                value={pickedListId}
                onChange={(e) => setPickedListId(e.target.value === '' ? '' : Number(e.target.value))}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm"
              >
                <option value="">— No list —</option>
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Template" hint="Optional. When picked, the template's HTML body is used and you can pass variables below.">
              <select
                value={templateId}
                onChange={(e) => {
                  const v = e.target.value === '' ? '' : Number(e.target.value);
                  setTemplateId(v);
                  if (v !== '') {
                    const t = templates.find((tt) => tt.id === v);
                    if (t && !subject) setSubject(t.subject);
                  }
                }}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm"
              >
                <option value="">— No template —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>

            {selectedTemplate && selectedTemplate.variables.length > 0 && (
              <Field
                label="Variables (JSON, optional)"
                hint={
                  <>
                    Template uses: {selectedTemplate.variables.map((v) => `{{${v}}}`).join(', ')}.
                    {' '}
                    For recipients in your contacts, <code className="text-[10px] bg-muted px-1 rounded">first_name</code>,{' '}
                    <code className="text-[10px] bg-muted px-1 rounded">last_name</code>,{' '}
                    <code className="text-[10px] bg-muted px-1 rounded">email</code> and any custom attributes
                    are merged automatically. The JSON below provides defaults / overrides for everyone.
                  </>
                }
              >
                <Textarea
                  value={variablesText}
                  onChange={(e) => setVariablesText(e.target.value)}
                  rows={3}
                  placeholder={`{ ${selectedTemplate.variables.map((v) => `"${v}": ""`).join(', ')} }`}
                />
              </Field>
            )}

            <Field label="Subject">
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Newsletter" />
            </Field>

            {!templateId && (
              <Field label="HTML body" hint="Plain HTML. Use {{ variable }} placeholders to template content.">
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  className="font-mono text-xs"
                  placeholder="<h1>Hello {{ name }}</h1>"
                />
              </Field>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {token?.credits ?? 0} credit(s) remaining.{' '}
                {(!token || token.credits <= 0) && (
                  <button type="button" className="underline" onClick={onOrderCredits}>
                    Order Credits
                  </button>
                )}
              </p>
              <Button type="submit" loading={sending}>
                <PaperAirplaneIcon className="h-4 w-4" /> Send email
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-3">
          <h3 className="text-base font-semibold">How sending works</h3>
          <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-5">
            <li>Each recipient gets their own envelope — the To: header never reveals the others.</li>
            <li>For recipients in your contacts, <code className="text-[10px] bg-muted px-1 rounded">first_name</code>, <code className="text-[10px] bg-muted px-1 rounded">last_name</code>, <code className="text-[10px] bg-muted px-1 rounded">email</code> and custom attributes are auto-merged into the template.</li>
            <li>1 credit per send op regardless of recipient count.</li>
            <li>Refunded automatically if every recipient fails.</li>
            <li>Pricing: <strong>{formatPrice(CREDIT_PRICE_HT)} excl. VAT</strong> per credit.</li>
          </ul>
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            Your sender address is <code className="bg-card px-1 rounded">{token?.from_address || '—'}</code>.
            The address is fixed; you can change the display name in <strong>API &amp; sender</strong>.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Contacts tab ───────────────────────────────────────────────────────────

interface ContactsProps {
  headers: Record<string, string>;
  contacts: Contact[];
  onChange: () => void;
  toast: (msg: string, type?: any) => void;
  confirm: (opts: any) => Promise<boolean>;
}

function ContactsTab({ headers, contacts, onChange, toast, confirm }: ContactsProps) {
  const [editing, setEditing] = useState<Contact | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState('');

  const remove = async (c: Contact) => {
    const ok = await confirm({
      title: 'Delete contact',
      message: `Delete ${c.email}? This will also remove them from all lists.`,
      variant: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    const res = await fetch(`${API_URL}/emailing/contacts/${c.id}`, { method: 'DELETE', headers });
    if (!res.ok) {
      toast('Failed to delete contact', 'error');
      return;
    }
    toast('Contact deleted', 'success');
    onChange();
  };

  const importCSV = async () => {
    const lines = importText.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length === 0) {
      toast('Paste at least one row.', 'error');
      return;
    }
    // Detect header row by common labels — accept email,first_name,last_name
    const first = lines[0].toLowerCase();
    const hasHeader = first.includes('email');
    const rows = (hasHeader ? lines.slice(1) : lines).map((line) => {
      const [email = '', first_name = '', last_name = ''] = line.split(/[,;\t]/).map((s) => s.trim());
      return { email, first_name, last_name };
    });
    const res = await fetch(`${API_URL}/emailing/contacts/import`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ contacts: rows }),
    });
    const data = await res.json();
    if (!res.ok) {
      toast(data.error || 'Import failed', 'error');
      return;
    }
    toast(`Imported ${data.inserted} new, updated ${data.updated}`, 'success');
    setImporting(false);
    setImportText('');
    onChange();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{contacts.length} contact(s).</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImporting(true)}>
            Import CSV
          </Button>
          <Button onClick={() => setCreating(true)}>
            <PlusIcon className="h-4 w-4" /> New contact
          </Button>
        </div>
      </div>

      {contacts.length === 0 ? (
        <EmptyState
          icon={<UsersIcon />}
          title="No contacts yet"
          description="Add contacts manually or import a CSV file."
          action={<Button onClick={() => setCreating(true)}>Add a contact</Button>}
        />
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono text-sm">{c.email}</TableCell>
                  <TableCell>
                    {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDateTime(c.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <RowMenu
                      items={[
                        { label: 'Edit', icon: <PencilIcon />, onSelect: () => setEditing(c) },
                        { label: 'Delete', icon: <TrashIcon />, danger: true, separatorBefore: true, onSelect: () => remove(c) },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {(creating || editing) && (
        <ContactDialog
          headers={headers}
          contact={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            onChange();
          }}
          toast={toast}
        />
      )}

      <Dialog open={importing} onClose={() => setImporting(false)} size="lg">
        <DialogHeader title="Import contacts (CSV)" onClose={() => setImporting(false)} />
        <DialogBody className="space-y-4">
          <div>
            <label
              htmlFor="csv-file-input"
              className="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground hover:bg-muted/40 cursor-pointer transition-colors"
            >
              <ArrowUpTrayIcon className="h-6 w-6" />
              <span><strong>Click to upload</strong> a .csv file</span>
              <span className="text-xs">or paste CSV text below</span>
            </label>
            <input
              id="csv-file-input"
              type="file"
              accept=".csv,text/csv,text/plain"
              className="sr-only"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const text = await file.text();
                  setImportText(text);
                } catch (err: any) {
                  toast(`Failed to read file: ${err.message}`, 'error');
                }
                // Reset so picking the same file twice still triggers onChange.
                e.target.value = '';
              }}
            />
          </div>
          <Field
            label="CSV content"
            hint="Columns: email, first_name, last_name. The first row may be a header."
          >
            <Textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              rows={10}
              className="font-mono text-xs"
              placeholder={`email,first_name,last_name\nalice@example.com,Alice,Smith`}
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => setImporting(false)}>
            Cancel
          </Button>
          <Button onClick={importCSV}>Import</Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

function ContactDialog({
  headers,
  contact,
  onClose,
  onSaved,
  toast,
}: {
  headers: Record<string, string>;
  contact: Contact | null;
  onClose: () => void;
  onSaved: () => void;
  toast: (msg: string, type?: any) => void;
}) {
  const [email, setEmail] = useState(contact?.email || '');
  const [firstName, setFirstName] = useState(contact?.first_name || '');
  const [lastName, setLastName] = useState(contact?.last_name || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const url = contact
        ? `${API_URL}/emailing/contacts/${contact.id}`
        : `${API_URL}/emailing/contacts`;
      const res = await fetch(url, {
        method: contact ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify({ email, first_name: firstName, last_name: lastName }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast(data.error || 'Failed to save contact', 'error');
        return;
      }
      toast(contact ? 'Contact updated' : 'Contact created', 'success');
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} size="md">
      <DialogHeader title={contact ? 'Edit contact' : 'New contact'} onClose={onClose} />
      <DialogBody className="space-y-3">
        <Field label="Email" required>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name">
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </Field>
          <Field label="Last name">
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Field>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submit} loading={saving} disabled={!email}>
          {contact ? 'Save' : 'Create'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── Lists tab ──────────────────────────────────────────────────────────────

interface ListsProps {
  headers: Record<string, string>;
  lists: ContactList[];
  contacts: Contact[];
  onChange: () => void;
  toast: (msg: string, type?: any) => void;
  confirm: (opts: any) => Promise<boolean>;
}

function ListsTab({ headers, lists, contacts, onChange, toast, confirm }: ListsProps) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ContactList | null>(null);
  const [managing, setManaging] = useState<ContactList | null>(null);

  const remove = async (l: ContactList) => {
    const ok = await confirm({
      title: 'Delete list',
      message: `Delete "${l.name}"? Contacts themselves are not removed.`,
      variant: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    const res = await fetch(`${API_URL}/emailing/lists/${l.id}`, { method: 'DELETE', headers });
    if (!res.ok) {
      toast('Failed to delete list', 'error');
      return;
    }
    toast('List deleted', 'success');
    onChange();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{lists.length} list(s).</p>
        <Button onClick={() => setCreating(true)}>
          <PlusIcon className="h-4 w-4" /> New list
        </Button>
      </div>

      {lists.length === 0 ? (
        <EmptyState
          icon={<RectangleStackIcon />}
          title="No lists yet"
          description="Group contacts into lists to easily target campaigns."
          action={<Button onClick={() => setCreating(true)}>Create a list</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lists.map((l) => (
            <Card key={l.id}>
              <CardContent className="p-5 space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-foreground truncate">{l.name}</h4>
                      <code
                        className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono text-muted-foreground shrink-0"
                        title="Use this ID in list_id when calling the API"
                      >
                        ID #{l.id}
                      </code>
                    </div>
                    {l.description && <p className="text-sm text-muted-foreground line-clamp-2">{l.description}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Badge variant="muted">{l.contact_count ?? 0} contact{l.contact_count === 1 ? '' : 's'}</Badge>
                    <RowMenu
                      items={[
                        { label: 'Manage contacts', icon: <UsersIcon />, onSelect: () => setManaging(l) },
                        { label: 'Edit', icon: <PencilIcon />, onSelect: () => setEditing(l) },
                        { label: 'Delete', icon: <TrashIcon />, danger: true, separatorBefore: true, onSelect: () => remove(l) },
                      ]}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <ListDialog
          headers={headers}
          list={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            onChange();
          }}
          toast={toast}
        />
      )}

      {managing && (
        <ListMembersDialog
          headers={headers}
          list={managing}
          contacts={contacts}
          onClose={() => setManaging(null)}
          toast={toast}
        />
      )}
    </div>
  );
}

function ListDialog({
  headers,
  list,
  onClose,
  onSaved,
  toast,
}: {
  headers: Record<string, string>;
  list: ContactList | null;
  onClose: () => void;
  onSaved: () => void;
  toast: (msg: string, type?: any) => void;
}) {
  const [name, setName] = useState(list?.name || '');
  const [description, setDescription] = useState(list?.description || '');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const url = list ? `${API_URL}/emailing/lists/${list.id}` : `${API_URL}/emailing/lists`;
      const res = await fetch(url, {
        method: list ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify({ name, description }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast(data.error || 'Failed to save list', 'error');
        return;
      }
      toast(list ? 'List updated' : 'List created', 'success');
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} size="md">
      <DialogHeader title={list ? 'Edit list' : 'New list'} onClose={onClose} />
      <DialogBody className="space-y-3">
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Newsletter subscribers" />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </Field>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim()}>
          {list ? 'Save' : 'Create'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function ListMembersDialog({
  headers,
  list,
  contacts,
  onClose,
  toast,
}: {
  headers: Record<string, string>;
  list: ContactList;
  contacts: Contact[];
  onClose: () => void;
  toast: (msg: string, type?: any) => void;
}) {
  const [members, setMembers] = useState<Contact[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    const res = await fetch(`${API_URL}/emailing/lists/${list.id}/contacts`, { headers });
    setMembers(res.ok ? (await res.json()) || [] : []);
    setLoading(false);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.id]);

  const memberIds = new Set(members.map((m) => m.id));
  const candidates = contacts.filter((c) => !memberIds.has(c.id));

  const addPicked = async () => {
    if (picked.size === 0) return;
    const res = await fetch(`${API_URL}/emailing/lists/${list.id}/contacts`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ contact_ids: Array.from(picked) }),
    });
    if (!res.ok) {
      toast('Failed to add contacts', 'error');
      return;
    }
    toast(`Added ${picked.size} contact(s)`, 'success');
    setPicked(new Set());
    reload();
  };

  const remove = async (c: Contact) => {
    const res = await fetch(`${API_URL}/emailing/lists/${list.id}/contacts/${c.id}`, {
      method: 'DELETE',
      headers,
    });
    if (!res.ok) {
      toast('Failed to remove', 'error');
      return;
    }
    reload();
  };

  return (
    <Dialog open onClose={onClose} size="2xl">
      <DialogHeader title={`Manage "${list.name}"`} onClose={onClose} />
      <DialogBody className="space-y-5">
        <div>
          <h4 className="text-sm font-semibold mb-2">Members ({members.length})</h4>
          {loading ? (
            <Skeleton className="h-20 w-full" />
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground">No contacts in this list yet.</p>
          ) : (
            <div className="max-h-60 overflow-auto rounded-md border border-border">
              <Table>
                <TableBody>
                  {members.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-mono text-xs">{c.email}</TableCell>
                      <TableCell>{[c.first_name, c.last_name].filter(Boolean).join(' ')}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => remove(c)}>
                          <TrashIcon className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <div>
          <h4 className="text-sm font-semibold mb-2">Add contacts</h4>
          {candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">All your contacts are already in this list.</p>
          ) : (
            <div className="max-h-60 overflow-auto rounded-md border border-border">
              <Table>
                <TableBody>
                  {candidates.map((c) => {
                    const isPicked = picked.has(c.id);
                    return (
                      <TableRow key={c.id}>
                        <TableCell className="w-10">
                          <input
                            type="checkbox"
                            checked={isPicked}
                            onChange={(e) => {
                              const next = new Set(picked);
                              if (e.target.checked) next.add(c.id);
                              else next.delete(c.id);
                              setPicked(next);
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">{c.email}</TableCell>
                        <TableCell>{[c.first_name, c.last_name].filter(Boolean).join(' ')}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button onClick={addPicked} disabled={picked.size === 0}>
          Add {picked.size || ''} selected
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── Templates tab ──────────────────────────────────────────────────────────

interface TemplatesProps {
  headers: Record<string, string>;
  templates: Template[];
  onChange: () => void;
  toast: (msg: string, type?: any) => void;
  confirm: (opts: any) => Promise<boolean>;
}

function TemplatesTab({ headers, templates, onChange, toast, confirm }: TemplatesProps) {
  const [editing, setEditing] = useState<Template | null>(null);
  const [creating, setCreating] = useState(false);

  const remove = async (t: Template) => {
    const ok = await confirm({
      title: 'Delete template',
      message: `Delete "${t.name}"? The HTML body will also be permanently deleted.`,
      variant: 'danger',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    const res = await fetch(`${API_URL}/emailing/templates/${t.id}`, { method: 'DELETE', headers });
    if (!res.ok) {
      toast('Failed to delete template', 'error');
      return;
    }
    toast('Template deleted', 'success');
    onChange();
  };

  // Duplicate fetches the source template's HTML body, then POSTs a brand-new
  // template with " (copy)" appended to the name. Server-side it's an
  // independent record — editing the copy doesn't affect the original.
  const duplicate = async (t: Template) => {
    try {
      const contentRes = await fetch(`${API_URL}/emailing/templates/${t.id}/content`, { headers });
      if (!contentRes.ok) throw new Error('Failed to load template body');
      const { body } = await contentRes.json();

      const createRes = await fetch(`${API_URL}/emailing/templates`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: `${t.name} (copy)`,
          subject: t.subject,
          description: t.description,
          sender_name: t.sender_name,
          body: body || '',
        }),
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to duplicate template');
      }
      toast(`Duplicated as "${t.name} (copy)"`, 'success');
      onChange();
    } catch (err: any) {
      toast(err.message, 'error');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          {templates.length} template(s).
        </p>
        <Button onClick={() => setCreating(true)}>
          <PlusIcon className="h-4 w-4" /> New template
        </Button>
      </div>

      {templates.length === 0 ? (
        <EmptyState
          icon={<DocumentTextIcon />}
          title="No templates yet"
          description="Build reusable HTML templates with {{ variables }}."
          action={<Button onClick={() => setCreating(true)}>Create a template</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((t) => (
            <Card key={t.id}>
              <CardContent className="p-5 space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-foreground truncate">{t.name}</h4>
                      <code
                        className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono text-muted-foreground shrink-0"
                        title="Use this ID in template_id when calling the API"
                      >
                        ID #{t.id}
                      </code>
                    </div>
                    {t.subject && <p className="text-xs text-muted-foreground truncate">Subject: {t.subject}</p>}
                    {t.sender_name && (
                      <p className="text-xs text-muted-foreground truncate">Sender: {t.sender_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Badge variant="muted">{t.variables.length} vars</Badge>
                    <RowMenu
                      items={[
                        { label: 'Edit', icon: <PencilIcon />, onSelect: () => setEditing(t) },
                        { label: 'Duplicate', icon: <DocumentDuplicateIcon />, onSelect: () => duplicate(t) },
                        { label: 'Delete', icon: <TrashIcon />, danger: true, separatorBefore: true, onSelect: () => remove(t) },
                      ]}
                    />
                  </div>
                </div>
                {t.variables.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {t.variables.map((v) => (
                      <code key={v} className="text-[10px] bg-muted px-1.5 py-0.5 rounded">
                        {`{{${v}}}`}
                      </code>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <TemplateDialog
          headers={headers}
          template={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            onChange();
          }}
          toast={toast}
        />
      )}
    </div>
  );
}

function TemplateDialog({
  headers,
  template,
  onClose,
  onSaved,
  toast,
}: {
  headers: Record<string, string>;
  template: Template | null;
  onClose: () => void;
  onSaved: () => void;
  toast: (msg: string, type?: any) => void;
}) {
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [description, setDescription] = useState(template?.description || '');
  const [senderName, setSenderName] = useState(template?.sender_name || '');
  const [body, setBody] = useState('');
  const [loadingBody, setLoadingBody] = useState(!!template);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!template) return;
    setLoadingBody(true);
    fetch(`${API_URL}/emailing/templates/${template.id}/content`, { headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load'))))
      .then((data) => setBody(data.body || ''))
      .catch(() => toast('Failed to load template body', 'error'))
      .finally(() => setLoadingBody(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id]);

  const submit = async () => {
    setSaving(true);
    try {
      const url = template
        ? `${API_URL}/emailing/templates/${template.id}`
        : `${API_URL}/emailing/templates`;
      const res = await fetch(url, {
        method: template ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify({ name, subject, description, sender_name: senderName, body }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast(data.error || 'Failed to save template', 'error');
        return;
      }
      toast(template ? 'Template updated' : 'Template created', 'success');
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} size="3xl">
      <DialogHeader title={template ? 'Edit template' : 'New template'} onClose={onClose} />
      <DialogBody className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Welcome email" />
          </Field>
          <Field label="Subject" hint="Subject can also use {{ variables }}.">
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Welcome, {{ first_name }}!" />
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Description">
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field
            label="Sender display name"
            hint="Optional. Overrides your default display name when sending with this template."
          >
            <Input
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              placeholder="e.g. Marketing Team"
            />
          </Field>
        </div>
        <TemplateBodyEditor body={body} onChange={setBody} loading={loadingBody} />
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim() || !body.trim()}>
          {template ? 'Save' : 'Create'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// TemplateBodyEditor lets the user toggle between editing the raw HTML and
// previewing the rendered result.
function TemplateBodyEditor({
  body,
  onChange,
  loading,
}: {
  body: string;
  onChange: (v: string) => void;
  loading: boolean;
}) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const previewHTML = useMemo(() => buildPreviewHTML(body), [body]);

  return (
    <Field
      label="HTML body"
      hint="Variables {{ first_name }}, {{ last_name }} and {{ email }} are auto-detected and listed on the template card."
    >
      <div className="space-y-2">
        <Tabs
          value={mode}
          onChange={(v) => setMode(v as 'edit' | 'preview')}
          variant="pill"
          items={[
            { value: 'edit', label: 'Edit HTML' },
            { value: 'preview', label: 'Preview' },
          ]}
        />
        {loading ? (
          <Skeleton className="h-80 w-full" />
        ) : mode === 'edit' ? (
          <Textarea
            value={body}
            onChange={(e) => onChange(e.target.value)}
            rows={20}
            className="font-mono text-xs"
            placeholder={`<!doctype html>\n<html><body>\n  <h1>Hello {{ first_name }}</h1>\n  <p>Thanks for joining.</p>\n</body></html>`}
          />
        ) : (
          <iframe
            title="Template preview"
            // `allow-same-origin` (without allow-scripts) lets external stylesheets,
            // web fonts, and images load while still preventing any JS from
            // executing — no XSS risk, but real-world CSS works.
            sandbox="allow-same-origin"
            srcDoc={previewHTML}
            className="w-full h-[480px] rounded-md border border-border bg-white"
          />
        )}
      </div>
    </Field>
  );
}

// buildPreviewHTML produces the HTML doc rendered inside the preview iframe.
//
// Highlighting `{{ var }}` placeholders is trickier than it looks: a naive
// string-replace breaks any template that uses variables inside attribute
// values (e.g. `<a href="{{ url }}">`) or inside `<style>`/`<script>` blocks.
// We use the browser's HTML parser to walk only text nodes — never attributes,
// never <style>/<script> contents — so the surrounding HTML stays intact.
function buildPreviewHTML(body: string): string {
  if (!body.trim()) {
    return '<!doctype html><html><body style="font-family:system-ui;color:#888;padding:24px">Empty template</body></html>';
  }

  const doc = new DOMParser().parseFromString(body, 'text/html');
  highlightVarsInTextNodes(doc.body);

  // DOMParser populates <html><head></head><body>...</body></html> even if the
  // user provided a fragment, so we always have a complete doc to serialize.
  return '<!doctype html>' + doc.documentElement.outerHTML;
}

const VAR_RE = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
const SKIP_TAGS = new Set(['STYLE', 'SCRIPT', 'NOSCRIPT', 'TEMPLATE']);

function highlightVarsInTextNodes(root: Node) {
  const ownerDoc = (root.ownerDocument ?? document) as Document;

  // Quick reject: parent is a no-render tag (style/script) or text has no `{{`.
  const walker = ownerDoc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const parent = n.parentElement;
      if (!parent || SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
      return n.textContent && n.textContent.includes('{{')
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  const targets: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) targets.push(node as Text);

  for (const text of targets) {
    const str = text.textContent ?? '';
    const fragment = ownerDoc.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    VAR_RE.lastIndex = 0;
    while ((match = VAR_RE.exec(str)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(ownerDoc.createTextNode(str.slice(lastIndex, match.index)));
      }
      const span = ownerDoc.createElement('span');
      span.setAttribute(
        'style',
        'background:#fde68a;color:#78350f;padding:0 4px;border-radius:3px;font-family:monospace;font-size:0.85em',
      );
      span.textContent = `{{${match[1]}}}`;
      fragment.appendChild(span);
      lastIndex = VAR_RE.lastIndex;
    }
    if (lastIndex < str.length) {
      fragment.appendChild(ownerDoc.createTextNode(str.slice(lastIndex)));
    }
    text.parentNode?.replaceChild(fragment, text);
  }
}

// ─── History tab ────────────────────────────────────────────────────────────

function HistoryTab({ emails }: { emails: SentEmail[] }) {
  if (emails.length === 0) {
    return (
      <EmptyState
        icon={<ClockIcon />}
        title="No emails sent yet"
        description="Sent emails appear here for the last 500 deliveries."
      />
    );
  }
  return (
    <Card className="overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Recipients</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead>From</TableHead>
            <TableHead>Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {emails.map((e) => (
            <TableRow key={e.id}>
              <TableCell>
                <StatusBadge status={e.status} />
                {e.status === 'failed' && e.error && (
                  <p className="text-xs text-destructive mt-1 max-w-xs truncate" title={e.error}>
                    {e.error}
                  </p>
                )}
              </TableCell>
              <TableCell className="font-mono text-xs max-w-xs truncate" title={e.recipients.join(', ')}>
                {e.recipients.slice(0, 2).join(', ')}
                {e.recipients.length > 2 && ` +${e.recipients.length - 2}`}
              </TableCell>
              <TableCell>{e.subject || <span className="text-muted-foreground">(no subject)</span>}</TableCell>
              <TableCell className="font-mono text-xs text-muted-foreground">{e.from}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{formatDateTime(e.created_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

// ─── Settings tab (API token + sender + code examples) ─────────────────────

interface SettingsProps {
  headers: Record<string, string>;
  token: UserToken | null;
  onChange: () => void;
  toast: (msg: string, type?: any) => void;
  confirm: (opts: any) => Promise<boolean>;
}

function SettingsTab({ headers, token, onChange, toast, confirm }: SettingsProps) {
  const ctx = useContext(KeycloakContext);
  const claims = ctx?.keycloak?.tokenParsed as any;
  const fullName = [claims?.given_name, claims?.family_name].filter(Boolean).join(' ');
  const namePlaceholder = fullName ? `e.g. ${fullName}` : 'e.g. First name + Last name';

  const [showToken, setShowToken] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [fromName, setFromName] = useState(token?.from_name || '');
  const [savingFromName, setSavingFromName] = useState(false);
  const [codeLang, setCodeLang] = useState<CodeLang>('curl');

  useEffect(() => {
    setFromName(token?.from_name || '');
  }, [token?.from_name]);

  const apiUrl = (typeof window !== 'undefined' && (window as any).APP_CONFIG?.API_URL) || API_URL;
  const tk = token?.token || '<your-api-token>';
  const fromAddress = token?.from_address || '<sender@edj-labs.com>';

  const codeExamples: Record<CodeLang, string> = {
    curl: `# 1. Single recipient with inline HTML
curl -X POST "${apiUrl}/email/send" \\
  -H "X-Api-Token: ${tk}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "recipients": ["alice@example.com"],
    "subject": "Hello",
    "html": "<h1>Hi!</h1>"
  }'

# 2. Using a saved template + variables (template_id from the Templates tab)
curl -X POST "${apiUrl}/email/send" \\
  -H "X-Api-Token: ${tk}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "recipients": ["alice@example.com", "bob@example.com"],
    "template_id": 1,
    "variables": { "first_name": "Alice" }
  }'

# 3. Send to a saved contact list (list_id from the Lists tab).
#    You can combine list_id with explicit recipients — they're deduplicated.
curl -X POST "${apiUrl}/email/send" \\
  -H "X-Api-Token: ${tk}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "list_id": 1,
    "template_id": 1
  }'`,
    python: `import requests

API_URL   = "${apiUrl}"
API_TOKEN = "${tk}"

def send_email(recipients=None, list_id=None, subject="", html="", template_id=None, variables=None):
    payload = {}
    if recipients:  payload["recipients"]  = recipients
    if list_id:     payload["list_id"]     = list_id
    if subject:     payload["subject"]     = subject
    if html:        payload["html"]        = html
    if template_id: payload["template_id"] = template_id
    if variables:   payload["variables"]   = variables
    r = requests.post(
        f"{API_URL}/email/send",
        headers={"X-Api-Token": API_TOKEN, "Content-Type": "application/json"},
        json=payload,
    )
    r.raise_for_status()
    return r.json()

# 1. Single recipient with inline HTML
send_email(recipients=["alice@example.com"], subject="Hello", html="<h1>Hi!</h1>")

# 2. Saved template + variables
send_email(
    recipients=["alice@example.com"],
    template_id=1,
    variables={"first_name": "Alice"},
)

# 3. Send to every contact in a saved list (list_id from the Lists tab)
send_email(list_id=1, template_id=1)`,
    javascript: `const API_URL   = "${apiUrl}";
const API_TOKEN = "${tk}";

async function sendEmail({ recipients, list_id, subject, html, template_id, variables }) {
  const r = await fetch(\`\${API_URL}/email/send\`, {
    method: "POST",
    headers: {
      "X-Api-Token": API_TOKEN,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ recipients, list_id, subject, html, template_id, variables }),
  });
  if (!r.ok) throw new Error((await r.json()).error || "send failed");
  return r.json();
}

// 1. Single recipient with inline HTML
await sendEmail({
  recipients: ["alice@example.com"],
  subject: "Hello",
  html: "<h1>Hi!</h1>",
});

// 2. Saved template + variables
await sendEmail({
  recipients: ["alice@example.com"],
  template_id: 1,
  variables: { first_name: "Alice" },
});

// 3. Send to every contact in a saved list (list_id from the Lists tab)
await sendEmail({ list_id: 1, template_id: 1 });`,
  };

  const regenerate = async () => {
    const ok = await confirm({
      title: 'Regenerate API token',
      message: 'Your current token will stop working immediately.',
      confirmLabel: 'Regenerate',
      variant: 'danger',
    });
    if (!ok) return;
    setRegenerating(true);
    try {
      const res = await fetch(`${API_URL}/emailing/token/regenerate`, { method: 'POST', headers });
      if (!res.ok) throw new Error('Failed to regenerate');
      toast('API token regenerated', 'success');
      onChange();
      setShowToken(true);
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setRegenerating(false);
    }
  };

  const saveFromName = async () => {
    setSavingFromName(true);
    try {
      const res = await fetch(`${API_URL}/emailing/from-name`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ from_name: fromName }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast(data.error || 'Failed to update display name', 'error');
        return;
      }
      toast('Display name updated', 'success');
      onChange();
    } finally {
      setSavingFromName(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-base font-semibold">Sender</h3>
          <p className="text-sm text-muted-foreground -mt-2">
            All mail goes out from a fixed address (set by the platform). You can customize the
            <strong> display name</strong> shown next to it in the recipient's inbox.
          </p>
          <Field label="Display name" hint="Leave blank to send with the bare address.">
            <Input
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder={namePlaceholder}
            />
          </Field>
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
            Resulting From header:{' '}
            <code className="text-foreground">
              {fromName
                ? `"${fromName}" <${token?.from_address?.replace(/^.*<|>.*$/g, '') || token?.from_address || ''}>`
                : token?.from_address?.replace(/^.*<|>.*$/g, '') || token?.from_address || '—'}
            </code>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveFromName} loading={savingFromName}>
              Save display name
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold">API token</h3>
            <div className="text-right">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">Credits</div>
              <div className="text-2xl font-bold tabular-nums">{token?.credits ?? 0}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Input
              readOnly
              type={showToken ? 'text' : 'password'}
              value={token?.token || ''}
              className="font-mono"
            />
            <Button variant="outline" size="icon" onClick={() => setShowToken(!showToken)}>
              {showToken ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => {
                navigator.clipboard.writeText(token?.token || '');
                toast('Token copied', 'success');
              }}
            >
              <ClipboardIcon className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Keep this token secret — anyone with it can spend your credits.
          </p>
          <button
            type="button"
            onClick={regenerate}
            disabled={regenerating}
            className="w-full inline-flex justify-center items-center rounded-md border border-destructive/40 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {regenerating ? 'Regenerating…' : 'Regenerate Token'}
          </button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h3 className="text-base font-semibold">API usage examples</h3>
          <p className="text-sm text-muted-foreground -mt-2">
            Authenticate with <code className="text-xs bg-muted px-1.5 py-0.5 rounded">X-Api-Token</code>. Sender address
            is enforced server-side: <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{fromAddress}</code>.
          </p>
          <div className="flex gap-2">
            {(['curl', 'python', 'javascript'] as CodeLang[]).map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setCodeLang(lang)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  codeLang === lang ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}
              >
                {lang === 'javascript' ? 'JavaScript' : lang.charAt(0).toUpperCase() + lang.slice(1)}
              </button>
            ))}
          </div>
          <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto text-sm leading-relaxed">
            <code>{codeExamples[codeLang]}</code>
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Buy credits dialog ────────────────────────────────────────────────────

function BuyCreditsDialog({
  open,
  onClose,
  headers,
  toast,
}: {
  open: boolean;
  onClose: () => void;
  headers: Record<string, string>;
  toast: (msg: string, type?: any) => void;
}) {
  const [credits, setCredits] = useState('1000');
  const [budget, setBudget] = useState((1000 * CREDIT_PRICE_HT).toFixed(2));
  const [submitting, setSubmitting] = useState(false);

  const handleCreditsChange = (val: string) => {
    setCredits(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n > 0) setBudget((n * CREDIT_PRICE_HT).toFixed(2));
  };
  const handleBudgetChange = (val: string) => {
    setBudget(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n > 0) setCredits(String(Math.round(n / CREDIT_PRICE_HT)));
  };

  const priceHT = parseFloat(budget) || 0;
  const priceTTC = Math.round(priceHT * (1 + TVA) * 100) / 100;
  const creditCount = parseInt(credits) || 0;

  const submit = async () => {
    if (creditCount <= 0 || priceHT <= 0) {
      toast('Enter a valid credit count or budget.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/orders/emailing`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ credits: creditCount, price_ht: priceHT }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create order');
      toast('Credit order created — an admin will fulfill it shortly.', 'success');
      onClose();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (v: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogHeader
        title="Order email credits"
        description={`${formatPrice(CREDIT_PRICE_HT)} per credit excl. VAT — 20% VAT applies`}
        onClose={onClose}
      />
      <DialogBody className="space-y-4">
        <Field label="Number of credits" hint="1 credit = 1 email send (any number of recipients).">
          <Input
            type="number"
            min={1}
            value={credits}
            onChange={(e) => handleCreditsChange(e.target.value)}
          />
        </Field>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <div className="flex-1 border-t border-border" />
          or enter a budget
          <div className="flex-1 border-t border-border" />
        </div>
        <Field label="Budget (excl. VAT)">
          <Input
            type="number"
            min={CREDIT_PRICE_HT}
            step={CREDIT_PRICE_HT}
            value={budget}
            onChange={(e) => handleBudgetChange(e.target.value)}
          />
        </Field>
        <div className="rounded-md bg-muted/30 border border-border p-3 text-sm space-y-1">
          <div className="flex justify-between">
            <span>{creditCount} credits × {formatPrice(CREDIT_PRICE_HT)}</span>
            <span>{fmt(priceHT)}</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>VAT (20%)</span>
            <span>{fmt(priceTTC - priceHT)}</span>
          </div>
          <div className="flex justify-between font-semibold pt-1 border-t border-border">
            <span>Total incl. VAT</span>
            <span>{fmt(priceTTC)}</span>
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={submitting}>
          Order {creditCount.toLocaleString('fr-FR')} credits
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
